import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TablePagination,
  Skeleton,
  TableSortLabel,
  Typography,
} from "@mui/material";
import React, { FunctionComponent } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  SortingState,
  OnChangeFn,
  flexRender,
} from "@tanstack/react-table";
import CustomerRow from "./CustomerRow";
import RefreshIcon from "@mui/icons-material/Refresh";
import { UserType } from "types/users/UserTypes";

type PropsType = {
  setExisting: Function;
  users: UserType[];
  total: number;
  isFetching: boolean;
  page: number;
  pageSize: number;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRefresh: () => void;
};

const columnHelper = createColumnHelper<UserType>();

const columns = [
  columnHelper.accessor("lastActionDate", {
    id: "last_action_date",
    header: "Last action",
    enableSorting: true,
  }),
  columnHelper.accessor((row) => `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim(), {
    id: "name",
    header: "Contact Person",
    enableSorting: false,
  }),
  columnHelper.accessor("created", {
    id: "created",
    header: "Created",
    enableSorting: true,
  }),
  columnHelper.accessor((row) => row.defaultBillingAddress?.phone, {
    id: "phone",
    header: "Phone",
    enableSorting: false,
  }),
  columnHelper.accessor("email", {
    id: "email",
    header: "Email",
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row.defaultBillingAddress?.company, {
    id: "company",
    header: "Company Name",
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row.defaultBillingAddress?.street, {
    id: "address",
    header: "Address",
    enableSorting: false,
  }),
];

const sortLabelSx = {
  color: "white !important",
  "& .MuiTableSortLabel-icon": { color: "white !important" },
};

const CustomerTable: FunctionComponent<PropsType> = ({
  setExisting,
  users,
  total,
  isFetching,
  page,
  pageSize,
  sorting,
  onSortingChange,
  onPageChange,
  onPageSizeChange,
  onRefresh,
}) => {
  const table = useReactTable({
    data: users,
    columns,
    state: { sorting },
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: Math.ceil(total / pageSize),
  });

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead sx={{ background: "var(--header)" }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const activeSortEntry = sorting.find(s => s.id === header.column.id);
                  return (
                    <TableCell key={header.id} align="left">
                      {canSort ? (
                        <TableSortLabel
                          active={!!activeSortEntry}
                          direction={activeSortEntry?.desc === false ? "asc" : "desc"}
                          onClick={header.column.getToggleSortingHandler()}
                          disabled={isFetching}
                          sx={sortLabelSx}
                        >
                          <Typography color="white" fontWeight="bold" variant="body1">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </Typography>
                        </TableSortLabel>
                      ) : (
                        <Typography color="white" fontWeight="bold" variant="body1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
                <TableCell>
                  <Tooltip title="Genindlæs">
                    <IconButton sx={{ color: "white" }} onClick={onRefresh}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableHead>
          {isFetching ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={columns.length + 1} sx={{ p: 0, border: 0 }}>
                  <Skeleton variant="rectangular" width="100%" height={pageSize * 54} />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : users.length > 0 ? (
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <CustomerRow
                  key={row.original.id}
                  customer={row.original}
                  setExisting={setExisting}
                />
              ))}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell colSpan={columns.length + 1} style={{ textAlign: "center" }}>
                  No customer found
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={total}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={(_: unknown, newPage: number) => onPageChange(newPage)}
        onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => onPageSizeChange(+e.target.value)}
      />
    </Paper>
  );
};

export default CustomerTable;
